/**
 * The pantry of the invented Familie Sommer (docs/18-vorratsbuch.md, V0-7): Vorratsschrank,
 * Kühlschrank, Gefriertruhe and Haushaltsschrank with stock, best-before dates (some soon) and rules
 * for buying again (some due). Used to show VorratsBuch, never for real data.
 */
import type { Receipt } from "./model.ts";
import { aisleOf, baseProduct } from "./base-products.ts";
import { pantryName, suggestedCountBy } from "./pantry.ts";

type Row = [profile: "lebensmittel" | "haushalt", base: string, brand: string, product: string, unit: "g" | "ml" | "Stk.", size: number | null, container: string, place: string, stock: number, days: number | null, reorderAt?: number, fillTo?: number];

// stock: containers (or pieces); days: best-before in days from now, null without.
const ROWS: readonly Row[] = [
  ["lebensmittel", "dosentomaten", "Mutti", "Polpa", "g", 400, "Dose", "vorrat/vorrat-regal", 4, 420, 2, 6],
  ["lebensmittel", "dosentomaten", "", "Geschälte Tomaten", "g", 400, "Dose", "vorrat/vorrat-regal", 2, 380],
  ["lebensmittel", "passierte-tomaten", "Mutti", "Passata", "g", 700, "Flasche", "vorrat/vorrat-regal", 1, 300, 1, 3],
  ["lebensmittel", "spaghetti", "Barilla", "Spaghetti n. 5", "g", 500, "Packung", "vorrat/vorrat-regal", 3, 500, 2, 5],
  ["lebensmittel", "reis", "", "Risottoreis Arborio", "g", 1000, "Packung", "vorrat/vorrat-regal", 1, 260],
  ["lebensmittel", "mehl", "", "Weizenmehl 405", "g", 1000, "Packung", "vorrat/vorrat-regal", 1, 200, 1, 2],
  ["lebensmittel", "zucker", "", "Zucker", "g", 1000, "Packung", "vorrat/vorrat-regal", 2, null],
  ["lebensmittel", "olivenoel", "", "Olivenöl nativ extra", "ml", 750, "Flasche", "vorrat/vorrat-regal", 1, 340, 1, 2],
  ["lebensmittel", "kichererbsen", "", "Kichererbsen", "g", 400, "Dose", "vorrat/vorrat-regal", 2, 600],
  ["lebensmittel", "kaffee", "", "Espresso ganze Bohne", "g", 1000, "Packung", "vorrat/vorrat-regal", 0, 180, 1, 2],
  ["lebensmittel", "milch", "", "H-Milch 1,5 %", "ml", 1000, "Packung", "kuehl/kuehl-fach", 3, 60, 4, 8],
  ["lebensmittel", "butter", "", "Butter", "g", 250, "Packung", "kuehl/kuehl-fach", 1, 9, 1, 2],
  ["lebensmittel", "joghurt", "", "Naturjoghurt", "g", 500, "Becher", "kuehl/kuehl-fach", 1, 3],
  ["lebensmittel", "parmesan", "", "Parmigiano Reggiano", "g", 200, "Packung", "kuehl/kuehl-fach", 1, 25],
  ["lebensmittel", "ei", "", "Eier Freiland", "Stk.", 10, "Packung", "kuehl/kuehl-fach", 1, 14, 1, 2],
  ["lebensmittel", "erbsen", "", "Erbsen", "g", 750, "Beutel", "truhe/truhe-fach", 1, 200],
  ["lebensmittel", "eis", "", "Vanilleeis", "ml", 900, "Packung", "truhe/truhe-fach", 2, 150],
  ["lebensmittel", "hackfleisch", "", "Rinderhack", "g", 500, "Packung", "truhe/truhe-fach", 2, 90],
  ["haushalt", "spuelmaschinentabs", "Claro", "Spülmaschinentabs", "Stk.", 40, "Packung", "putzschrank/putzschrank-fach", 1, null, 1, 2],
  ["haushalt", "toilettenpapier", "", "Toilettenpapier", "Stk.", 8, "Packung", "putzschrank/putzschrank-fach", 1, null, 1, 3],
  ["haushalt", "waschmittel", "", "Colorwaschmittel", "ml", 1500, "Flasche", "putzschrank/putzschrank-fach", 1, null],
  ["haushalt", "muellbeutel", "", "Müllbeutel 35 l", "Stk.", 30, "Rolle", "putzschrank/putzschrank-fach", 2, null],
];

const DAY = 86_400_000;
const iso = (at: number) => new Date(at).toISOString().slice(0, 10);

export interface DemoPantryRow {
  id: string;
  fields: Record<string, unknown>;
  receipts: Omit<Receipt, "id" | "recordedAt">[];
}

/** The demo pantry as articles and receipts a week ago; base products that the copy lacks are skipped. */
export function demoPantry(now: number, newId: () => string): DemoPantryRow[] {
  const out: DemoPantryRow[] = [];
  for (const [profile, baseId, brand, product, unit, size, container, place, stock, days, reorderAt, fillTo] of ROWS) {
    const base = baseProduct(baseId);
    if (!base) continue;
    const fields: Record<string, unknown> = {
      profile,
      product,
      ...(brand ? { brand } : {}),
      base: base.id,
      aisle: aisleOf(base.aisle)?.name ?? "",
      unit,
      ...(size !== null ? { size } : {}),
      container,
      tracking: profile === "lebensmittel" ? "lot" : "quantity",
    };
    // Eggs count one by one; their rule is in eggs then, not in boxes.
    const pieces = unit === "Stk." && suggestedCountBy(base) !== undefined;
    if (pieces) fields.countBy = "Stück";
    const per = pieces && size !== null ? size : 1;
    if (reorderAt !== undefined) fields.reorderAt = reorderAt * per;
    if (fillTo !== undefined) fields.fillTo = fillTo * per;
    fields.name = pantryName(fields);
    fields.category = fields.aisle;
    const id = newId();
    const lager = place.split("/")[0]!;
    const receipts: DemoPantryRow["receipts"] =
      stock > 0
        ? [
            {
              kind: "in",
              at: now - 7 * DAY,
              article: id,
              lager,
              location: place,
              qty: stock * (size ?? 1),
              reason: "purchase",
              ...(days !== null && profile === "lebensmittel" ? { lotInfo: { bestBefore: iso(now + days * DAY) } } : {}),
            },
          ]
        : [];
    out.push({ id, fields, receipts });
  }
  return out;
}
